/**
 * Mobile-OK 본인확인 서비스
 * 드림시큐리티 Mobile-OK 표준창 연동
 *
 * MOKToken 방식: SDK → JS 콜백으로 mokToken 전달 → 백엔드 /result API 호출
 *
 * @see https://mobile-ok.com/guide/mok_std_guide/
 */

import jwt_decode from 'jwt-decode';
import {
  logSDKLoadSuccess,
  logSDKLoadError,
  logAuthStart,
  logAuthError,
  addAuthBreadcrumb,
} from './sentryAuth';

// 백엔드 API URL (SDK가 직접 호출하므로 항상 절대 URL 필요)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.connectdoctor.co.kr';

declare global {
  interface Window {
    MOBILEOK?: IMobileOK;
  }
}

interface IMobileOK {
  process: (
    requestUrl: string,
    deviceType: string,
    callbackFuncName: string,
  ) => void;
}

// 인증 요청 파라미터
export interface MobileOKRequestParams {
  serviceType?: 'telcoAuth' | 'telcoAuth-LMS'; // SMS 또는 LMS
  usageCode: string; // 이용목적 코드 (01001: 회원가입, 01007: 본인확인 등)
  userId?: number; // 사용자 ID
  returnUrl?: string; // 콜백 URL (구버전 호환용)
}

// 인증 결과 (백엔드에서 복호화 후 전달)
export interface MobileOKResult {
  resultCode: string; // 결과코드 (0000: 성공)
  resultMsg: string; // 결과메시지
  txId?: string; // 거래 ID
  ci?: string; // CI (연계정보)
  di?: string; // DI (중복가입확인정보)
  name?: string; // 성명
  birthDate?: string; // 생년월일 (YYYYMMDD)
  gender?: string; // 성별 (M/F)
  nationalInfo?: string; // 내외국인 (L: 내국인, F: 외국인)
  mobileNo?: string; // 휴대폰번호
  mobileCo?: string; // 통신사
}

// SDK 로드 상태
type SDKStatus = 'idle' | 'loading' | 'loaded' | 'error';

// 인증 환경 정보
export interface MobileOKAuthEnvironment {
  deviceType: 'WB' | 'MB';
  useRedirect: boolean;  // redirect 방식 사용 여부
  isInAppBrowser: boolean;
  inAppBrowserType: string | null;
  isIOSSafari: boolean;
  isFlutterApp: boolean;
}

class MobileOKService {
  private sdkStatus: SDKStatus = 'idle';
  private sdkLoadPromise: Promise<void> | null = null;

  // 개발/운영 환경 구분
  private readonly isDev =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost';

  // SDK URL (개발/운영)
  private readonly SDK_URL = this.isDev
    ? 'https://scert.mobile-ok.com/resources/js/index.js'
    : 'https://cert.mobile-ok.com/resources/js/index.js';

  /**
   * Mobile-OK SDK 스크립트 로드
   */
  loadScript(): Promise<void> {
    if (this.sdkStatus === 'loaded') {
      return Promise.resolve();
    }

    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkStatus = 'loading';
    this.sdkLoadPromise = new Promise((resolve, reject) => {
      // 이미 로드된 경우
      if (window.MOBILEOK) {
        this.sdkStatus = 'loaded';
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.SDK_URL;
      script.async = true;

      script.onload = () => {
        this.sdkStatus = 'loaded';
        console.log('[MobileOK] SDK loaded successfully');
        logSDKLoadSuccess('mobileOK');
        resolve();
      };

      script.onerror = () => {
        this.sdkStatus = 'error';
        this.sdkLoadPromise = null;
        const error = new Error('[MobileOK] Failed to load SDK');
        console.error(error);
        logSDKLoadError('mobileOK', error);
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  /**
   * 본인확인 표준창 실행
   * @param requestUrl 백엔드 요청 URL
   * @param deviceType 디바이스 타입 (WB=PC웹, MB=모바일웹)
   * @param callbackFuncName 콜백 함수명 (문자열)
   */
  async process(
    requestUrl: string,
    deviceType: string = 'WB',
    callbackFuncName: string = '',
  ): Promise<void> {
    await this.loadScript();

    if (!window.MOBILEOK) {
      const error = new Error('[MobileOK] SDK not available');
      logAuthError(error, { authMethod: 'mobile' }, { 'auth.stage': 'sdk-check' });
      throw error;
    }

    console.log('[MobileOK] Calling MOBILEOK.process with:', {
      requestUrl,
      deviceType,
      callbackFuncName,
    });

    window.MOBILEOK.process(requestUrl, deviceType, callbackFuncName);
  }

  /**
   * SDK 로드 상태 확인
   */
  isLoaded(): boolean {
    return this.sdkStatus === 'loaded' && !!window.MOBILEOK;
  }

  /**
   * 현재 환경 확인 (개발/운영)
   */
  getEnvironment(): 'development' | 'production' {
    return this.isDev ? 'development' : 'production';
  }

  /**
   * 로컬스토리지 JWT에서 사용자 ID 추출 (폴백용)
   */
  private getUserIdFromToken(): number | undefined {
    try {
      const token = localStorage.getItem('token');
      if (!token) return undefined;
      const decoded = jwt_decode<{ id?: number }>(token);
      return decoded?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * 클라이언트 txId 생성
   * 형식: MOK_{userId}_{timestamp}_{random}
   */
  private generateClientTxId(userId: number): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `MOK_${userId}_${timestamp}_${random}`;
  }

  /**
   * 디바이스 타입 감지
   * WB: PC 웹, MB: 모바일 웹
   */
  detectDeviceType(): 'WB' | 'MB' {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    return isMobile ? 'MB' : 'WB';
  }

  /**
   * 인증 환경 감지
   * - deviceType: WB(PC) 또는 MB(모바일)
   * - useRedirect: 인앱 브라우저/iOS Safari에서는 redirect 방식 사용
   */
  detectAuthEnvironment(): MobileOKAuthEnvironment {
    const ua = navigator.userAgent;
    const lowerUA = ua.toLowerCase();

    // 모바일 여부 판단
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua);

    // 인앱 브라우저 감지
    const inAppPatterns: Record<string, RegExp> = {
      'KakaoTalk': /kakaotalk/i,
      'Naver': /naver\(/i,
      'Instagram': /instagram/i,
      'Facebook': /fban|fbav/i,
      'Line': /line\//i,
      'Band': /band\//i,
      'Twitter': /twitter/i,
    };

    let inAppBrowserType: string | null = null;
    for (const [name, pattern] of Object.entries(inAppPatterns)) {
      if (pattern.test(ua)) {
        inAppBrowserType = name;
        break;
      }
    }

    // iOS Safari 감지
    const isIOSSafari = /iphone|ipad/i.test(ua) &&
                        /safari/i.test(ua) &&
                        !/chrome|crios|fxios/i.test(ua);

    // Flutter WebView 감지
    const isFlutterApp = localStorage.getItem('mobileApp') !== null;

    // 일반 WebView 감지
    const isGenericWebView = (lowerUA.includes('iphone') || lowerUA.includes('ipad')) &&
                             !lowerUA.includes('safari');

    // redirect 사용 조건
    const useRedirect = !!(
      inAppBrowserType ||
      isFlutterApp ||
      isIOSSafari ||
      isGenericWebView
    );

    return {
      deviceType: isMobile ? 'MB' : 'WB',
      useRedirect,
      isInAppBrowser: !!inAppBrowserType,
      inAppBrowserType,
      isIOSSafari,
      isFlutterApp,
    };
  }

  /**
   * 휴대폰 인증 시작
   *
   * Mobile-OK는 EZ-IOK와 달리 반드시 returnUrl이 필요합니다.
   * SDK가 인증 완료 후 returnUrl로 mokToken을 POST합니다.
   *
   * 인앱 브라우저/iOS Safari에서는 redirect 방식 사용
   */
  async startAuth(params: MobileOKRequestParams): Promise<void> {
    const userId = params.userId || this.getUserIdFromToken() || 0;

    // 프론트엔드에서 txId를 먼저 생성
    const clientTxId = this.generateClientTxId(userId);

    // 환경 감지
    const env = this.detectAuthEnvironment();

    console.log('[MobileOK] Auth environment:', env);

    // redirect 방식이면 복귀 경로 저장
    if (env.useRedirect) {
      try {
        sessionStorage.setItem('auth_return_path', window.location.href);
        console.log('[MobileOK] Saved return path:', window.location.href);
      } catch (e) {
        console.warn('[MobileOK] Failed to save return path:', e);
      }
    }

    // callback 방식일 때만 sessionStorage에 txId 백업 저장
    if (!env.useRedirect) {
      try {
        sessionStorage.setItem('mobileok_txId', clientTxId);
        console.log('[MobileOK] Generated clientTxId:', clientTxId);
      } catch (storageError) {
        // sessionStorage 사용 불가 시 redirect 방식으로 전환
        console.warn('[MobileOK] sessionStorage unavailable, switching to redirect mode');
        env.useRedirect = true;
        addAuthBreadcrumb('sessionStorage unavailable, using redirect mode', 'sdk', { error: String(storageError) }, 'warning');
      }
    }

    // returnUrl에 clientTxId와 retType 포함
    // - clientTxId: redirect 방식에서 txId 유지
    // - retType: 백엔드 콜백에서 callback/redirect 모드 구분
    const baseReturnUrl = `${API_BASE}/func/mobileok/callback`;
    const retType = env.useRedirect ? 'redirect' : 'callback';
    const returnUrl = `${baseReturnUrl}?clientTxId=${clientTxId}&retType=${retType}`;

    // localStorage에서 인증 토큰 가져오기
    const accessToken = localStorage.getItem('token');

    // 백엔드 요청 URL 구성
    const queryParams = new URLSearchParams({
      usageCode: params.usageCode,
      serviceType: params.serviceType || 'telcoAuth',
      clientTxId: clientTxId,
      returnUrl: returnUrl,
    });

    // 인증 토큰이 있으면 쿼리 파라미터로 추가
    if (accessToken) {
      queryParams.append('access_token', accessToken);
    }

    const requestUrl = `${API_BASE}/func/mobileok/request?${queryParams.toString()}`;

    console.log('[MobileOK] Starting auth with:', {
      requestUrl,
      deviceType: env.deviceType,
      useRedirect: env.useRedirect,
      clientTxId,
      returnUrl,
      isInAppBrowser: env.isInAppBrowser,
    });

    // Sentry 인증 시작 로깅
    logAuthStart('mobile', {
      txId: clientTxId,
      userId,
      deviceType: env.deviceType,
      useRedirect: env.useRedirect,
      isInAppBrowser: env.isInAppBrowser,
      inAppBrowserType: env.inAppBrowserType,
      isIOSSafari: env.isIOSSafari,
      usageCode: params.usageCode,
      serviceType: params.serviceType || 'telcoAuth',
    });

    try {
      // redirect 방식이면 콜백 함수명을 빈 문자열로 전달
      const callbackFuncName = env.useRedirect ? '' : 'mobileOKCallback';
      await this.process(requestUrl, env.deviceType, callbackFuncName);
    } catch (error) {
      logAuthError(error instanceof Error ? error : new Error(String(error)), {
        authMethod: 'mobile',
        deviceType: env.deviceType,
        useRedirect: env.useRedirect,
        usageCode: params.usageCode,
      }, { 'auth.stage': 'sdk-call' });
      throw error;
    }
  }
}

export default new MobileOKService();